import ArrowBack from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PeaceOfMind from 'components/ui/PeaceOfMind';
import React, { useEffect, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import { styles } from 'theme/theme';

import { appConfig } from '../../config/runtimeConfig';
import { useRouter } from '../../hooks/useRouter';
import { usePostUsersResendConfirmEmailMutation } from '../../redux/ellipsisApi';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import authSlice from '../../redux/reducers/authSlice';

import Footer from './Footer';
import HeaderNav from './HeaderNav';
import MobileNav from './MobileNav';

const Hero = styled('section')(() => ({
  width: '100%',
  backgroundSize: 'cover',
  marginBottom: '30px',
}));

interface ContentWrapperInterface {
  maxwidth?: number | string | false;
}

const ContentWrapper = styled('section')<ContentWrapperInterface>((props) => ({
  margin: '0 auto',
  maxWidth: props.maxwidth ? `${props.maxwidth}px` : '',
  width: '100%',
}));

interface OwnProps {
  showSimple?: boolean;
  showBackBtn?: boolean;
  children: React.ReactNode;
  disableFooter?: boolean;
  contentMargin?: boolean;
  heroContent?: any;
  maxWidth?: number | false;
  showBanners?: boolean;
  paddingBottom?: boolean;
  paddingTop?: boolean;
  allowScroll?: boolean;
  displayBottomNav?: boolean;
  showCurrencyConvertor?: boolean;
  showReassurance?: boolean;
}

const PageWithHeader = (props: OwnProps) => {
  const {
    showSimple = false,
    showBackBtn = false,
    disableFooter = false,
    heroContent,
    maxWidth = 2520,
    showBanners = true,
    paddingBottom = true,
    paddingTop = false,
    allowScroll = true,
    displayBottomNav = true,
    showCurrencyConvertor,
    showReassurance = false,
  } = props;
  const dispatch = useAppDispatch();
  const showVerifyEmailBanner = useAppSelector(
    (state) => state.auth.showVerifyEmailBanner
  );
  const showCompleteProfileBanner = useAppSelector(
    (state) => state.auth.showCompleteProfileBanner
  );

  const router = useRouter();
  const goBack = () => router.goBack();

  const StyledPageBox = useMemo(() => {
    return styled(Box)(({ theme }) => ({
      [theme.breakpoints.down('sm')]: {
        paddingBottom: paddingBottom ? styles.smallPagePadding : undefined,
        paddingTop: paddingTop ? styles.smallPagePadding : undefined,
        paddingLeft: styles.smallPagePadding,
        paddingRight: styles.smallPagePadding,
      },
      [theme.breakpoints.down('md')]: {
        paddingBottom: paddingBottom ? styles.smallPagePadding : undefined,
        paddingTop: paddingTop ? styles.smallPagePadding : undefined,
        paddingLeft: styles.smallPagePadding,
        paddingRight: styles.smallPagePadding,
      },
      [theme.breakpoints.up('md')]: {
        paddingBottom: paddingBottom ? styles.smallPagePadding : undefined,
        paddingTop: paddingTop ? styles.smallPagePadding : undefined,
        paddingLeft: styles.mediumPagePadding,
        paddingRight: styles.mediumPagePadding,
      },
      [theme.breakpoints.up('lg')]: {
        paddingBottom: paddingBottom ? styles.mediumPagePadding : undefined,
        paddingTop: paddingTop ? styles.mediumPagePadding : undefined,
        paddingLeft: styles.largePagePadding,
        paddingRight: styles.largePagePadding,
      },
    }));
  }, [paddingBottom, paddingTop]);

  const [
    resendConfirmEmail,
    { isSuccess: isResendSuccess, isLoading: isResendLoading },
  ] = usePostUsersResendConfirmEmailMutation();
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    dispatch(authSlice.actions.checkVerifyEmailBanner());
  });

  const closeVerifyEmailBanner = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    dispatch(authSlice.actions.hideVerifyEmailBanner());
  };

  const closeCompleteProfileBanner = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    dispatch(authSlice.actions.hideCompleteProfileBanner());
  };

  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href =
      appConfig.APP_BRAND === 'ellipsis' ? '/favicon.ico' : '/hireaboat.ico';
  }, []);

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-end"
      sx={{ paddingTop: 'env(safe-area-inset-top)', minHeight: '100vh' }}
    >
      <HeaderNav
        goBack={goBack}
        showSimple={showSimple}
        maxwidth={maxWidth ? maxWidth : 'unset'}
        showCurrencyConvertor={showCurrencyConvertor}
        showBackBtn={showBackBtn}
      />
      <ToastContainer />
      {showBanners &&
        user &&
        !user?.emailVerified &&
        !(isResendLoading || isResendSuccess) && (
          <Box
            position="fixed"
            padding="5px 10px"
            sx={{
              boxSizing: 'border-box',
              zIndex: 2,
              top: { xs: styles.mobileAppBarHeight, sm: styles.appBarHeight },
            }}
            width="100%"
          >
            <Collapse in={showVerifyEmailBanner}>
              <Alert
                severity="warning"
                sx={{ cursor: 'pointer' }}
                onClick={() => !isResendLoading && resendConfirmEmail({})}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="medium"
                    onClick={closeVerifyEmailBanner}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                <AlertTitle> Your email is not verified</AlertTitle>
                Click here to resend the link
              </Alert>
            </Collapse>
          </Box>
        )}
      {showBanners &&
        user &&
        user?.emailVerified &&
        (!user?.profilePicture || (user?.biography?.length ?? 0) < 3) && (
          <Box
            position="fixed"
            padding="5px 10px"
            top={styles.appBarHeight}
            sx={{ boxSizing: 'border-box', zIndex: 2 }}
            width="100%"
          >
            <Collapse in={showCompleteProfileBanner}>
              <Alert
                severity="warning"
                sx={{ cursor: 'pointer' }}
                onClick={() => router.push('/user-profile')}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="medium"
                    onClick={closeCompleteProfileBanner}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                <AlertTitle>You haven't completed your profile.</AlertTitle>
                Click here to go to your profile and add a profile picture and
                biography to complete your profile.
              </Alert>
            </Collapse>
          </Box>
        )}
      {heroContent && (
        <Hero>
          <ContentWrapper maxwidth={false}>{heroContent}</ContentWrapper>
        </Hero>
      )}

      <ContentWrapper
        as="main"
        maxwidth={maxWidth ? maxWidth : styles.maxWidth}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: {
            xs: 'flex-start',
            sm: disableFooter ? 'flex-start' : 'space-between',
          },
        }}
      >
        <StyledPageBox
          sx={{
            flexGrow: 0,
            maxHeight: !allowScroll
              ? `calc(100vh - ${styles.appBarHeight}px)`
              : undefined,
            height: !allowScroll
              ? `calc(100vh - ${styles.appBarHeight}px)`
              : 'auto',
          }}
        >
          {showBackBtn && (
            <Button
              color="primary"
              onClick={goBack}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': { color: '#009FE3' },
              }}
            >
              <ArrowBack />
              <Typography align="left">Go Back</Typography>
            </Button>
          )}
          {props.children}
        </StyledPageBox>
      </ContentWrapper>
      {showReassurance && <PeaceOfMind />}
      {!disableFooter && <Footer maxwidth={maxWidth ? maxWidth : 'unset'} />}
      {/* CSS rather than useIsMobile() decides mobile-only chrome here and
      above, so server-rendered pages send the right layout for the viewport
      instead of desktop-then-mobile after hydration (see HeaderNav). */}
      {displayBottomNav && (
        <Box sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
          <Box sx={{ height: styles.bottomBarHeight, width: '100%' }} />
          <MobileNav />
        </Box>
      )}
    </Box>
  );
};

export default PageWithHeader;
